/**
 * Deterministic stand-in for Math.random() in server-rendered components.
 *
 * React 18 reconciles the SSR markup against the first client render, so a
 * value drawn from Math.random() during render is by definition different on
 * the two sides — a hydration hazard, and a re-render hazard too, since the
 * value changes again on every subsequent render. Seeding from props that are
 * identical on both sides (bubble coordinates, an item index...) keeps the
 * numbers stable across the server/client boundary and across re-renders while
 * still looking random.
 *
 * mulberry32: 32-bit, no state outside the closure, same output everywhere.
 */
export const createSeededRandom = (seed: number) => {
  let state = Math.abs(Math.floor(seed)) >>> 0 || 0x9e3779b9;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** Mixes a handful of numbers (coordinates, sizes, indexes) into one seed. */
export const mixSeed = (...values: number[]) =>
  values.reduce(
    (acc, value) => (Math.imul(acc ^ (value | 0), 0x27d4eb2d) + 0x165667b1) >>> 0,
    0x811c9dc5
  ) >>> 0;
